import {
  BlockStatus,
  SETTING_VALUE_SCHEMAS,
  SettingKey,
  isDraftExpired,
  rehomeSpan,
  settingId,
  RecordType,
} from '@wiztime/core';
import { ValidationError } from '../errors';
import { TEMPLATE_REFERENCE_DATE } from './day-bounds';
import type { WriteSession } from './session';

/**
 * Change the personal-day start and re-home stored data as one step.
 *
 * A block's wall minutes are counted from 00:00 of its `personalDate`, so its absolute time is
 * fixed. Under the new start, every wall minute `m` belongs to personal date `date + k` with
 * `k = floor((m - newDayStart) / 1440)` and becomes `m - 1440 * k` relative to it. A block that
 * crosses a new day boundary is split there: the earliest piece keeps the block's id, later
 * pieces get new ids and copy project, status, note, time zone and draft source. Drafts are
 * handled like blocks, except expired drafts: they are invisible, and moving one to another date
 * could make it show again, so they are purged (as a system deletion, not part of the undo step)
 * instead of moved. Templates (daily slots) are re-homed the same way without a date, so a
 * template that straddles the new boundary becomes two templates. Pieces of different days
 * never overlap, so no overlap check is needed. Returns the number of records written.
 */
export async function changeDayStart(session: WriteSession, newDayStartMin: number): Promise<number> {
  const parsed = SETTING_VALUE_SCHEMAS.dayStartMin.safeParse(newDayStartMin);
  if (!parsed.success) {
    throw new ValidationError(
      'Invalid personal-day start',
      parsed.error.issues.map((issue) => issue.message),
    );
  }
  const settings = await session.settings();
  if (settings.dayStartMin === parsed.data) return 0;

  let written = 0;
  for (const block of await session.tx.listByType(RecordType.TimeBlock)) {
    const data = block.data;
    if (data === null) continue;
    if (data.status === BlockStatus.Draft && isDraftExpired(data.personalDate, settings, session.nowMs)) {
      await session.purge(block);
      continue;
    }
    const pieces = rehomeSpan(data.personalDate, data.startMin, data.endMin, parsed.data);
    const unchanged =
      pieces.length === 1 &&
      pieces[0]?.personalDate === data.personalDate &&
      pieces[0].startMin === data.startMin &&
      pieces[0].endMin === data.endMin;
    if (unchanged) continue;
    for (const [index, piece] of pieces.entries()) {
      await session.write(RecordType.TimeBlock, index === 0 ? block.id : session.newId(), {
        ...data,
        ...piece,
      });
      written += 1;
    }
  }

  for (const template of await session.tx.listByType(RecordType.Template)) {
    const data = template.data;
    if (data === null) continue;
    const pieces = rehomeSpan(TEMPLATE_REFERENCE_DATE, data.startMin, data.endMin, parsed.data);
    const unchanged =
      pieces.length === 1 &&
      pieces[0]?.personalDate === TEMPLATE_REFERENCE_DATE &&
      pieces[0].startMin === data.startMin &&
      pieces[0].endMin === data.endMin;
    if (unchanged) continue;
    for (const [index, piece] of pieces.entries()) {
      await session.write(RecordType.Template, index === 0 ? template.id : session.newId(), {
        ...data,
        startMin: piece.startMin,
        endMin: piece.endMin,
      });
      written += 1;
    }
  }

  // Check-in logs are identified by occurrence and stay as they are (data dictionary §3.2); the
  // day start that interprets legacy check-in data is frozen before it changes.
  if ((await session.find(settingId(SettingKey.CheckinLegacyDayStartMin)))?.data == null) {
    await session.write(RecordType.Setting, settingId(SettingKey.CheckinLegacyDayStartMin), {
      key: SettingKey.CheckinLegacyDayStartMin,
      value: settings.dayStartMin,
    });
    written += 1;
  }

  await session.write(RecordType.Setting, settingId(SettingKey.DayStartMin), {
    key: SettingKey.DayStartMin,
    value: parsed.data,
  });
  return written + 1;
}
